import {
  AreaTag,
  CreateAreaTagData,
  UpdateAreaTagData,
  AreaTagApiResponse,
} from '../types/area-tags';
import { GET, POST, PUT, PATCH, DELETE } from '@/shared/api/requests';

const API_BASE_URL = 'area-tags';

const normalizeAreaTagApiResponse = (
  data: AreaTag | AreaTagApiResponse
): AreaTagApiResponse => {
  if ('id' in data) {
    return {
      status: 'success',
      area_tag_id: data.id,
      data,
    };
  }
  return data;
};

const areaTagsUrl = (params: Record<string, string | number | undefined> = {}) => {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  });

  const query = search.toString();
  return `${API_BASE_URL}/${query ? `?${query}` : ''}`;
};

const areaTagUrl = (
  areaTagId: number,
  params: Record<string, string | number | undefined> = {}
) => {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  });

  const query = search.toString();
  return `${API_BASE_URL}/${areaTagId}/${query ? `?${query}` : ''}`;
};

// Helper function to create FormData for multipart requests
const createFormData = (
  data: CreateAreaTagData | UpdateAreaTagData,
  isUpdate = false
): FormData => {
  const formData = new FormData();

  // Prepare JSON data
  const jsonData: Record<string, unknown> = {};
  if ('name' in data && data.name) jsonData.name = data.name;
  if ('coordinates' in data && data.coordinates) jsonData.coordinates = data.coordinates;
  if ('marker_ids' in data && data.marker_ids) jsonData.marker_ids = data.marker_ids;
  if ('photo_ids' in data && data.photo_ids) jsonData.photo_ids = data.photo_ids;
  if ('text_position' in data && data.text_position !== undefined) jsonData.text_position = data.text_position;
  if ('text_size' in data && data.text_size !== undefined) jsonData.text_size = data.text_size !== null ? Math.round(data.text_size) : null;
  if ('text_rotation' in data && data.text_rotation !== undefined) jsonData.text_rotation = data.text_rotation;
  if (!isUpdate && 'layout_id' in data) jsonData.layout_id = data.layout_id;

  formData.append('data', JSON.stringify(jsonData));

  // Add photo if provided
  if (data.photo) {
    formData.append('photo', data.photo);
  }

  return formData;
};

/**
 * Fetch all area tags for a given layout
 */
export const fetchAreaTags = async (layoutId: string, projectId: string): Promise<AreaTag[]> => {
  try {
    const response = await GET(areaTagsUrl({ layout_id: layoutId, project_id: projectId }));
    const data = response.data;
    return Array.isArray(data) ? data : data.results || [];
  } catch (error) {
    console.error('Error fetching area tags:', error);
    throw error;
  }
};

/**
 * Fetch all area tags for a project (across all layouts)
 */
export const fetchAreaTagsByProject = async (projectId: string, layoutId: string): Promise<AreaTag[]> => {
  try {
    const response = await GET(areaTagsUrl({ layout_id: layoutId, project_id: projectId }));
    const data = response.data;
    return Array.isArray(data) ? data : data.results || [];
  } catch (error) {
    console.error('Error fetching area tags by project:', error);
    throw error;
  }
};

/**
 * Fetch a specific area tag by ID
 */
export const fetchAreaTag = async (
  areaTagId: number,
  layoutId: string,
  projectId: string
): Promise<AreaTag> => {
  try {
    const response = await GET(areaTagUrl(areaTagId, { layout_id: layoutId, project_id: projectId }));
    return response.data;
  } catch (error) {
    console.error('Error fetching area tag:', error);
    throw error;
  }
};

/**
 * Create a new area tag
 */
export const createAreaTag = async (
  data: CreateAreaTagData,
  projectId: string
): Promise<AreaTagApiResponse> => {
  try {
    // Convert Point[] format to number[][] format that backend expects
    const coordinatesArray = data.coordinates.map((point) => [point.x, point.y]);

    // Send JSON directly instead of FormData for simple area creation
    const payload = {
      name: data.name,
      layout: data.layout,
      coordinates: coordinatesArray,
      marker_ids: data.marker_ids || [],
      photo_ids: data.photos || [],
    };
    const response = await POST(areaTagsUrl({ project_id: projectId }), payload);
    return normalizeAreaTagApiResponse(response.data as AreaTag | AreaTagApiResponse);
  } catch (error) {
    console.error('Error creating area tag:', error);
    throw error;
  }
};

/**
 * Update an existing area tag (full update)
 */
export const updateAreaTag = async (
  areaTagId: number,
  data: UpdateAreaTagData,
  layoutId: string,
  projectId: string
): Promise<AreaTagApiResponse> => {
  try {
    const formData = createFormData(data, true);
    const response = await PUT(areaTagUrl(areaTagId, { layout_id: layoutId, project_id: projectId }), formData);
    return response.data;
  } catch (error) {
    console.error('Error updating area tag:', error);
    throw error;
  }
};

export const patchAreaTag = async (
  areaTagId: number,
  data: Partial<UpdateAreaTagData>,
  layoutId: string,
  projectId: string
): Promise<AreaTagApiResponse> => {
  try {
    let payload: FormData | Record<string, unknown>;

    if (data.photo) {
      // Use FormData for file uploads
      payload = createFormData(data as UpdateAreaTagData, true);
    } else {
      // Use JSON for simple updates
      const jsonData: Record<string, unknown> = {};
      if (data.name !== undefined) jsonData.name = data.name;
      if (data.coordinates !== undefined) jsonData.coordinates = data.coordinates;
      if (data.marker_ids !== undefined) jsonData.marker_ids = data.marker_ids;
      if (data.photo_ids !== undefined) jsonData.photo_ids = data.photo_ids;
      if (data.text_position !== undefined) jsonData.text_position = data.text_position;
      if (data.text_size !== undefined) jsonData.text_size = data.text_size !== null ? Math.round(data.text_size) : null;
      if (data.text_rotation !== undefined) jsonData.text_rotation = data.text_rotation;
      payload = jsonData;
    }

    const response = await PATCH(areaTagUrl(areaTagId, { layout_id: layoutId, project_id: projectId }), payload);
    return response.data;
  } catch (error) {
    console.error('Error patching area tag:', error);
    throw error;
  }
};





/**
 * Delete an area tag
 */
export const deleteAreaTag = async (
  areaTagId: number,
  layoutId: string,
  projectId: string
): Promise<void> => {
  try {
    await DELETE(areaTagUrl(areaTagId, { layout_id: layoutId, project_id: projectId }));
    // DELETE returns 204 No Content on success, so no data to return
  } catch (error) {
    console.error('Error deleting area tag:', error);
    throw error;
  }
};

/**
 * Upload area tag photo separately (if needed)
 */
export const uploadAreaTagPhoto = async (
  areaTagId: number,
  photo: File,
  layoutId: string,
  projectId: string
): Promise<AreaTagApiResponse> => {
  try {
    const formData = new FormData();
    formData.append('data', JSON.stringify({})); // Empty data object
    formData.append('photo', photo);

    const response = await PATCH(areaTagUrl(areaTagId, { layout_id: layoutId, project_id: projectId }), formData);
    return response.data;
  } catch (error) {
    console.error('Error uploading area tag photo:', error);
    throw error;
  }
};
